import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  ClockIcon,
  CubeIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';
import type { AnalysisResults } from '@printability/shared-types';
import { ModelViewer } from '../components/ModelViewer';

interface AnalysisData {
  analysisId: string;
  filename: string;
  results: AnalysisResults;
  processingTime: number;
}

export const Analysis: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [fileData, setFileData] = useState<ArrayBuffer | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try to get analysis data from sessionStorage first
    const storedAnalysis = sessionStorage.getItem('latestAnalysis');
    const storedFileData = sessionStorage.getItem('latestFileData');

    if (storedAnalysis) {
      try {
        const data = JSON.parse(storedAnalysis);
        if (data.analysisId === id) {
          setAnalysisData(data);

          // Load file data if available
          if (storedFileData) {
            try {
              const fileDataArray = JSON.parse(storedFileData);
              const arrayBuffer = new Uint8Array(fileDataArray).buffer;
              setFileData(arrayBuffer);
            } catch (error) {
              console.error('Failed to parse stored file data:', error);
            }
          }

          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('Failed to parse stored analysis:', error);
      }
    }

    // If no stored data, show loading state
    // In a real app, you'd fetch from the API here
    setLoading(false);
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!analysisData) {
    return (
      <div className="space-y-6">
        <div>
          <Link to="/upload" className="inline-flex items-center text-blue-600 hover:text-blue-500">
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Upload
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">Analysis Not Found</h1>
          <p className="mt-1 text-sm text-gray-500">
            The analysis results for ID {id} could not be found.
          </p>
        </div>
        <div className="card text-center py-12">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">No Analysis Data</h3>
          <p className="mt-1 text-sm text-gray-500">
            Please upload a new model to get analysis results.
          </p>
          <div className="mt-6">
            <Link to="/upload" className="btn btn-primary">
              Upload New Model
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { results, filename, processingTime } = analysisData;
  const score = results.printabilityScore;

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'medium':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'low':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      default:
        return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/upload" className="inline-flex items-center text-blue-600 hover:text-blue-500">
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Upload
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-2">Analysis Results</h1>
        <p className="mt-1 text-sm text-gray-500">
          Printability analysis for <span className="font-medium">{filename}</span>
        </p>
      </div>

      {/* Overall Score */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Printability Score</h2>
            <p className="text-sm text-gray-500">Overall assessment of print success likelihood</p>
          </div>
          <div className={`text-right ${getScoreColor(score)}`}>
            <div className="text-3xl font-bold">{score}</div>
            <div className="text-sm">out of 100</div>
          </div>
        </div>

        <div className="mt-4">
          <div className="bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all duration-500 ${
                score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${score}%` }}
            ></div>
          </div>
        </div>

        <div className="mt-4 flex items-center text-sm text-gray-500">
          <ClockIcon className="h-4 w-4 mr-1" />
          Processed in {processingTime}ms
        </div>
      </div>

      {/* Geometry Metrics */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Geometry Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <CubeIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900">
              {results.metrics.volume.toFixed(1)}
            </div>
            <div className="text-sm text-gray-500">Volume (mm³)</div>
          </div>

          <div className="text-center">
            <ScaleIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-gray-900">
              {results.metrics.surfaceArea.toFixed(1)}
            </div>
            <div className="text-sm text-gray-500">Surface Area (mm²)</div>
          </div>

          <div className="text-center">
            <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
              <span className="text-blue-600 font-bold text-sm">XYZ</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {results.metrics.boundingBox[0].toFixed(1)} × {results.metrics.boundingBox[1].toFixed(1)} × {results.metrics.boundingBox[2].toFixed(1)}
            </div>
            <div className="text-sm text-gray-500">Dimensions (mm)</div>
          </div>
        </div>
      </div>

      {/* 3D Model Viewer */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">3D Model View</h3>
        <ModelViewer
          fileData={fileData || undefined}
          fileName={filename}
          issues={results.issues
            .filter(issue => issue.position) // Filter out issues without position
            .map(issue => ({
              position: issue.position as [number, number, number],
              type: issue.type,
              severity: issue.severity,
              description: issue.description,
            }))}
        />
      </div>

      {/* Issues */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Detected Issues ({results.issues.length})
        </h3>

        {results.issues.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-3" />
            <h4 className="text-lg font-medium text-gray-900">No Issues Detected!</h4>
            <p className="text-gray-500">Your model looks great for 3D printing.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {results.issues.map((issue, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  {getSeverityIcon(issue.severity)}
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900 capitalize">
                        {issue.type.replace('_', ' ')}
                      </h4>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        issue.severity === 'high' ? 'bg-red-100 text-red-800' :
                        issue.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {issue.severity}
                      </span>
                    </div>
                    <p className="text-gray-600 mt-1">{issue.description}</p>
                    {issue.suggestedFix && (
                      <p className="text-blue-600 text-sm mt-2">
                        💡 <strong>Suggestion:</strong> {issue.suggestedFix}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recommendations */}
      {results.recommendations.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recommendations</h3>
          <ul className="space-y-2">
            {results.recommendations.map((recommendation, index) => (
              <li key={index} className="flex items-start space-x-2">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{recommendation}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Next Steps</h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/upload" className="btn btn-primary">
            Analyze Another Model
          </Link>
          <button
            onClick={() => window.print()}
            className="btn btn-outline"
          >
            Print Report
          </button>
          <button
            onClick={() => {
              const data = JSON.stringify(analysisData, null, 2);
              const blob = new Blob([data], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `analysis-${filename}-${id}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="btn btn-outline"
          >
            Download Results
          </button>
        </div>
      </div>
    </div>
  );
};
